// ============================================================================
// "Escalation raised" — the transporter's marker for a shipment somebody flagged
// ON THE DAY ITS TRIP RUNS. Same small inline shape as the express / unloading
// tags, carried beside the weight rather than in a column of its own.
//
// The SAME-DAY condition is the whole point. A truck can carry an escalation
// raised weeks ago on an earlier attempt; what a transporter reading today's
// trips needs to see is a problem raised against TODAY's run. Anything older is
// history and would only add noise to every row it touches.
//
// "Today" here is the trip's DISPATCH DATE, in IST — the same date this page
// filters, groups and judges pickup SLAs on, so one row can't be "same day" by
// one measure and not another.
//
// That condition lives in `escalation_excuse`, beside the rule that reads it:
// this tag being on a row is exactly what makes its escalation eligible to excuse
// that row's SLA breach, so the two must never drift apart.
//
// RESOLVING DOES NOT TAKE THE TAG AWAY. The day's record is the day's record: a
// transporter reading their trips must see the problem that was raised against a
// run whether or not the desk has since closed the ticket, and the SLA verdict
// hanging off this tag must not move hours later because somebody tidied up. The
// tooltip says it was resolved; the tag stays put.
// ============================================================================
import { CheckCircleFilled, WarningFilled } from "@ant-design/icons";
import { Tag, Tooltip } from "antd";
import React from "react";
import { escalationOf, raisedOnTripDate } from "./escalation_excuse";
import { fmtIST } from "../format";
import { LogisticsOrderRow } from "../types";

const EscalationRaisedTag: React.FC<{ row: LogisticsOrderRow }> = ({ row }) => {
  if (!raisedOnTripDate(row)) return null;
  const e = escalationOf(row)!;
  // A closed escalation keeps the tag and the word, and wears the muted volcano step rather than
  // red — still plainly an escalation, no longer a live alarm competing with the open ones.
  const resolved = !!e.resolved_at;
  return (
    <Tooltip
      title={
        <>
          {e.issue_type || "Escalation"} — raised {fmtIST(e.raised_at, "D MMM, h:mm A")} by{" "}
          {e.raised_by || "the system"}
          {e.count > 1 ? ` (${e.count} times)` : ""}
          {resolved ? <div style={{ marginTop: 4 }}>Resolved {fmtIST(e.resolved_at, "D MMM, h:mm A")} — it still counts against this trip's day.</div> : null}
          {e.remarks ? <div style={{ marginTop: 4 }}>“{e.remarks}”</div> : null}
        </>
      }
    >
      <Tag
        color={resolved ? "volcano" : "red"}
        icon={resolved ? <CheckCircleFilled /> : <WarningFilled />}
        style={{ marginInlineEnd: 0, cursor: "help" }}
      >
        escalation raised
      </Tag>
    </Tooltip>
  );
};

export default EscalationRaisedTag;
